import React, { FC, useMemo } from "react";
import { MultiSelect } from "@src/lib/ui/MultiSelect";
import { useUserMultiSelect_EmployeesQuery } from "@src/features/users/ui/operations.generated";
import { MinimalEmployeeFragment } from "./ui/operations.generated";
import { UserType } from "@src/types.generated";
import Avatar from "@src/lib/ui/Avatar";

type Props = {
  defaultUserIds?: string[];
  setEmployees?: (employees: MinimalEmployeeFragment[]) => void;
  excludeEmployeeIds?: Set<string>;
  placeholder?: string;
  userTypes?: UserType[];
};

const UserMultiSelect: FC<Props> = ({
  defaultUserIds,
  setEmployees,
  excludeEmployeeIds,
  placeholder = "Search people...",
  userTypes = [],
}) => {
  const { data } = useUserMultiSelect_EmployeesQuery({
    variables: {
      userTypes,
    },
  });
  const allEmployees = useMemo(() => data?.Employees.objects || [], [data]);
  const availableEmployees = useMemo(
    () =>
      allEmployees.filter(
        (e) => !excludeEmployeeIds || !excludeEmployeeIds.has(e.id.toString()),
      ),
    [allEmployees, excludeEmployeeIds],
  );
  const options = useMemo(
    () =>
      availableEmployees.map((employee) => ({
        label: employee.name || "",
        value: employee.id.toString(),
        avatar: (
          <Avatar person={employee} size={16} className="cursor-[inherit]" />
        ),
      })),
    [availableEmployees],
  );
  const defaultValue = useMemo(() => {
    if (!defaultUserIds) return [];
    const defaultSelectedEmployees = allEmployees.filter((employee) =>
      defaultUserIds.includes(employee.id.toString()),
    );
    return defaultSelectedEmployees.map((e) => e.id.toString());
  }, [allEmployees, defaultUserIds]);
  const handleValueChange = (selectedValues: string[]) => {
    if (!setEmployees) return;
    const selectedEmployees = allEmployees.filter((employee) =>
      selectedValues.includes(employee.id.toString()),
    );
    setEmployees(selectedEmployees);
  };
  return (
    <MultiSelect
      options={options}
      onValueChange={setEmployees ? handleValueChange : () => {}}
      defaultValue={defaultValue}
      placeholder={placeholder}
      dialogPopover={true}
    />
  );
};

export default UserMultiSelect;
